import { useMemo } from "react";
import { formatCurrency } from "../../Utils/helpers";

function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M7 1L8.854 5.09L13.5 5.635L10.125 8.79L11.09 13.5L7 11.105L2.91 13.5L3.875 8.79L0.5 5.635L5.146 5.09L7 1Z"
        fill="#39FF14"
      />
    </svg>
  );
}

function UnlockedBadge() {
  return (
    <div className="st-qualified-badge">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path
          d="M8.5 2.5L4 7.5L1.5 5"
          stroke="#54FF99"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>Unlocked</span>
    </div>
  );
}

function formatTrackerAmount(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return formatCurrency(0, 0);
  return formatCurrency(value, 0);
}

export default function MoveInBonusTracker({ plan, tracker }) {
  const {
    total = 0,
    spend = 0,
    is_unlocked: isUnlocked = false,
    rebate_amount: rebateAmount = 0,
    desc_1: desc1 = "",
    desc_2: desc2 = "",
    desc_3: desc3 = "",
  } = tracker ?? {};

  const isUnlimited = plan === "unlimited";
  const spendAmount = Number(spend) || 0;
  const totalAmount = Number(total) || 0;

  const pct = useMemo(() => {
    if (isUnlocked) return 100;
    if (totalAmount <= 0) return 0;
    return Math.min(100, Math.round((spendAmount / totalAmount) * 100));
  }, [isUnlocked, spendAmount, totalAmount]);

  const statusLabel = useMemo(() => {
    if (isUnlocked || !isUnlimited) return null;
    if (spendAmount === 0) return "Not set up";
    return "Deposit under rent";
  }, [isUnlocked, isUnlimited, spendAmount]);

  const amountDisplay = isUnlocked
    ? formatTrackerAmount(rebateAmount)
    : formatTrackerAmount(spendAmount);

  const amountSublabel = isUnlocked
    ? "added to your balance"
    : isUnlimited
      ? "deposited to Stake"
      : "spent on Stake Debit";

  const progressRight = useMemo(() => {
    if (isUnlocked) return "Complete";
    if (isUnlimited && spendAmount === 0) {
      return `${formatTrackerAmount(totalAmount)} rent`;
    }
    return `${formatTrackerAmount(spendAmount)} / ${formatTrackerAmount(totalAmount)}`;
  }, [isUnlocked, isUnlimited, spendAmount, totalAmount]);

  const showCta = !isUnlocked && Boolean(desc2 || desc3);

  return (
    <div className={`move-in-bonus-tracker${statusLabel ? " has-status-label" : ""}`}>
      {statusLabel && <span className="mibt-status-label">{statusLabel}</span>}
      <div className={`spend-tracker mibt-card${showCta ? " has-cta" : ""}`}>
        <div className="st-title-row">
          <span className="st-title">Move-in Bonus</span>
          {isUnlocked && <UnlockedBadge />}
        </div>
        <div className="st-amount-row">
          <span className="st-amount">{amountDisplay}</span>
          <span className="st-amount-sub">{amountSublabel}</span>
        </div>
        <div className="st-progress-section">
          <div className="st-bar-bg">
            <div
              className={`st-bar-fill${pct === 0 ? " neon" : ""}${isUnlocked ? " qualified" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="st-progress-labels">
            <span className={`st-progress-desc${isUnlocked ? " qualified" : ""}`}>{desc1}</span>
            <span className={`st-progress-pct${isUnlocked ? " qualified" : spendAmount > 0 ? " active" : ""}`}>
              {progressRight}
            </span>
          </div>
        </div>
        {showCta && (
          <>
            <div className="st-divider" />
            <div className="st-cta-row mibt-cta-row">
              <div className="st-cta-icon">
                <StarIcon />
              </div>
              <div className="st-cta-text mibt-cta-text">
                {desc2 ? <span className="st-cta-gray">{desc2}</span> : null}
                {desc3 ? <span className="st-cta-green">{desc3}</span> : null}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
